/**
 * main.js - Entry Point chính của Voxel Anim Studio
 * Điều phối khởi tạo Three.js, State, Modes, Navigation, Customizer, Interaction & Storyboard UI.
 */

import { state } from './state.js';
import { initScene, handleResize, updateViewOffset } from './scene-setup.js';
import { startAnimationLoop } from './loop.js';
import { exposeTestHooks } from './hooks.js';
import { startVideoRecording, showToastNotification } from './recording.js';
import { SceneAudioManager } from '../audio/scene-audio-manager.js';

import { setupStudioAvatar, clearAvatars } from './modes/studio-mode.js';
import { setupVietnamCityMode, clearCityScene } from './modes/city-mode.js';
import { setupDramaMode } from './modes/drama-mode.js';
import { setupAnimalsMode, initAnimalListeners } from './modes/animals-mode.js';
import { setupLabMode, clearLabBench } from './modes/lab-mode.js';
import { setupWalkerMode } from './modes/walker-mode.js';
import { switchMode, initSceneTabListeners } from './modes/mode-router.js';

import { initNavigationUI } from './ui/navigation-ui.js';
import { initCustomizerUI } from './ui/customizer-ui.js';
import { initInteractionUI } from './ui/interaction-ui.js';
import { initStoryboardUI } from './ui/storyboard-ui.js';
import { initCardThumbnails } from './ui/thumbnail-ui.js';
import { ensureCanvasFonts } from '../core/fonts.js';

// 1. Khởi tạo Three.js Scene, Camera, Controls, Lights
const container = document.getElementById('canvas-container');
initScene(container, state);

// 2. Audio Manager toàn cục
const globalAudioManager = new SceneAudioManager();

// 3. Khởi tạo các UI Subsystems (Wiring)
let customizerUI = null;
let interactionUI = null;
let storyboardUI = null;
let navigationUI = null;

function setupStudioAvatarWrap(presetKey, customProps = {}) {
  return setupStudioAvatar(presetKey, customProps, state, (key) => {
    if (interactionUI) interactionUI.updatePresetReactionsUI(key);
  });
}

function switchModeWrap(mode, options = {}) {
  switchMode(
    mode,
    state,
    {
      clearCityScene,
      clearLabBench,
      clearAvatars,
      setupStudioAvatar: setupStudioAvatarWrap,
      switchStudioPanel: (panel) => navigationUI?.switchStudioPanel(panel),
      setupAnimalsMode: (s) =>
        setupAnimalsMode(s, {
          clearCityScene,
          clearLabBench,
          clearAvatars,
          switchStudioPanel: (p) => navigationUI?.switchStudioPanel(p)
        }),
      setupWalkerMode: (s) => setupWalkerMode(s, { clearAvatars, clearCityScene, clearLabBench }),
      setupLabMode: (s) => setupLabMode(s, { clearAvatars, clearCityScene, clearLabBench }),
      setupVietnamCityMode: (isDrama, s) => setupVietnamCityMode(isDrama, s, clearAvatars),
      setupDramaMode: (s) =>
        setupDramaMode(s, {
          clearAvatars,
          clearCityScene,
          clearLabBench,
          closeMobileDrawer: () => navigationUI?.closeMobileDrawer(),
          updateMobileFabVisibility: () => navigationUI?.updateMobileFabVisibility(),
          startVideoRecording: (st) => startVideoRecording(st || state, switchModeWrap)
        }),
      updateMobileFabVisibility: () => navigationUI?.updateMobileFabVisibility()
    },
    options
  );
}

// Khởi tạo các bộ điều khiển UI
interactionUI = initInteractionUI(state, globalAudioManager);

customizerUI = initCustomizerUI(state, {
  setupStudioAvatar: setupStudioAvatarWrap
});

storyboardUI = initStoryboardUI(state, {
  switchMode: switchModeWrap,
  setupStudioAvatar: setupStudioAvatarWrap,
  syncCustomizerChips: (opts) => customizerUI?.syncCustomizerChips(opts),
  switchStudioPanel: (panel) => navigationUI?.switchStudioPanel(panel)
});

navigationUI = initNavigationUI(state, {
  switchMode: switchModeWrap,
  renderCharacterRoster: () => storyboardUI?.renderCharacterRoster(),
  renderStoryboardShots: () => storyboardUI?.renderStoryboardShots()
});

// Gán listeners cho Scene tabs
initSceneTabListeners(state, switchModeWrap);

// Khởi tạo điều khiển Động vật (Animals Mode)
initAnimalListeners(state);

// Cập nhật view offset để né panel ở desktop ngay khi khởi tạo
updateViewOffset(state);

// Window resize listener
window.addEventListener('resize', () => handleResize(state));

// 4. Expose Test Hooks cho Playwright E2E & Visual Testing
exposeTestHooks(state, {
  setupStudioAvatar: setupStudioAvatarWrap,
  clearAvatars: () => clearAvatars(state),
  loadAndApplyProject: (data, src) => storyboardUI.loadAndApplyProject(data, src),
  showToastNotification
});

// 5. Bắt đầu Main Animation Loop ngay lập tức để canvas không bị trống
startAnimationLoop(state);

// 6. Khởi tạo Fonts & Bootstrap Studio
(async () => {
  await ensureCanvasFonts();

  const urlProject = state.projectManager.decodeProjectFromUrl();
  if (urlProject) {
    if (urlProject.storyboard && urlProject.storyboard.length > 0) {
      switchModeWrap('drama');
    } else {
      setupStudioAvatarWrap(state.currentPresetKey);
    }
    storyboardUI.loadAndApplyProject(urlProject, 'AI Agent');

    setTimeout(() => {
      showToastNotification(`✨ Đã nạp kịch bản "${urlProject.title || 'Dự án'}" từ AI Agent!`);
    }, 500);
  } else {
    setupStudioAvatarWrap('TEACHER');
  }

  // 7. Khởi tạo Card Thumbnails cho Customizer UI (PR 10)
  initCardThumbnails();
})();
